import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthProvider';
import Modal from '../common/Modal';

export const LoginModal = ({ isOpen, onClose }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [formError, setFormError] = useState('');
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        if (!email || !password) {
            setFormError('Please fill in all fields');
            return;
        }

        const result = await login(email, password);

        if (result.success) {
            onClose();
            navigate('/profile');
        } else {
            setFormError(result.message || 'Login failed');
        }
    };

    const openRegisterModal = () => {
        onClose();
        document.dispatchEvent(new CustomEvent('open-register-modal'));
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Log In">
            {formError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {formError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-white text-sm font-bold mb-2" htmlFor="email">
                        Email
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="email"
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>
                <div>
                    <label className="block text-white text-sm font-bold mb-2" htmlFor="password">
                        Password
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="password"
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </div>
                <div className="flex flex-col gap-4">
                    <button
                        className="my-2 bg-accent/80 hover:bg-accent text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        type="submit"
                    >
                        Log In
                    </button>
                    <div className="text-center">
                        <p className="inline text-sm text-gray-700 dark:text-gray-300 font-medium">
                            Don't have an account?{' '}
                            <button
                                type="button"
                                onClick={openRegisterModal}
                                className="text-accent underline hover:no-underline bg-transparent hover:bg-transparent"
                            >
                                Sign Up
                            </button>
                        </p>
                    </div>
                </div>
            </form>
        </Modal>
    );
};

export const RegisterModal = ({ isOpen, onClose }) => {
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [formError, setFormError] = useState('');
    const [success, setSuccess] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);

    const { register } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        // Basic validation
        if (!username || !email || !password || !confirmPassword) {
            setFormError('Please fill in all required fields');
            return;
        }

        if (password !== confirmPassword) {
            setFormError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setFormError('Password must be at least 6 characters');
            return;
        }

        if (!acceptTerms) {
            setFormError('You must accept the Terms and Conditions and Privacy Policy');
            return;
        }

        const result = await register(username, email, password, firstName, lastName);

        if (result.success) {
            setSuccess(true);
            // Close modal after 2 seconds
            setTimeout(() => {
                onClose();
                document.dispatchEvent(new CustomEvent('open-login-modal'));
            }, 2000);
        } else {
            setFormError(result.message || 'Registration failed');
        }
    };

    const openLoginModal = () => {
        onClose();
        document.dispatchEvent(new CustomEvent('open-login-modal'));
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create Account">
            {success ? (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    Registration successful! Redirecting to login...
                </div>
            ) : formError ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {formError}
                </div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-white text-sm font-bold mb-2" htmlFor="username">
                        Username <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="username"
                        type="text"
                        placeholder="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-white text-sm font-bold mb-2" htmlFor="firstName">
                            First Name
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            id="firstName"
                            type="text"
                            placeholder="First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-white text-sm font-bold mb-2" htmlFor="lastName">
                            Last Name
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            id="lastName"
                            type="text"
                            placeholder="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-white text-sm font-bold mb-2" htmlFor="email">
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="email"
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>
                <div>
                    <label className="block text-white text-sm font-bold mb-2" htmlFor="password">
                        Password <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="password"
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                </div>
                <div>
                    <label className="block text-white text-sm font-bold mb-2" htmlFor="confirmPassword">
                        Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </div>

                <div className="flex items-start !my-6">
                    <div className="flex items-center h-5 mt-2">
                        <input
                            id="acceptTerms"
                            type="checkbox"
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            className="w-4 h-4 border border-gray-300 rounded bg-gray-50 cursor-pointer checked:!bg-black checked:!border-black focus:ring-0 focus:outline-none focus:shadow-none"
                            required
                        />
                    </div>
                    <div className="ml-2 text-sm font-medium text-white">
                        I accept the{' '}
                        <Link to="/terms" className="text-accent underline hover:no-underline" onClick={(e) => { e.stopPropagation(); window.open('/terms', '_blank') }}>
                            Terms and Conditions
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-accent underline hover:no-underline" onClick={(e) => { e.stopPropagation(); window.open('/privacy', '_blank') }}>
                            Privacy Policy
                        </Link>
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    <button
                        className="bg-accent/80 hover:bg-accent text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                        type="submit"
                    >
                        Create Account
                    </button>
                    <div className="text-center">
                        <p className="inline text-sm text-accent dark:text-gray-300 font-medium">
                            Already have an account?{' '}
                            <button
                                type="button"
                                onClick={openLoginModal}
                                className="text-accent underline hover:no-underline bg-transparent hover:bg-transparent"
                            >
                                Log In
                            </button>
                        </p>
                    </div>
                </div>
            </form>
        </Modal>
    );
};

const AuthModals = () => {
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [registerModalOpen, setRegisterModalOpen] = useState(false);

    React.useEffect(() => {
        const openLoginModal = () => setLoginModalOpen(true);
        const openRegisterModal = () => setRegisterModalOpen(true);

        document.addEventListener('open-login-modal', openLoginModal);
        document.addEventListener('open-register-modal', openRegisterModal);

        return () => {
            document.removeEventListener('open-login-modal', openLoginModal);
            document.removeEventListener('open-register-modal', openRegisterModal);
        };
    }, []);

    return (
        <>
            <LoginModal
                isOpen={loginModalOpen}
                onClose={() => setLoginModalOpen(false)}
            />
            <RegisterModal
                isOpen={registerModalOpen}
                onClose={() => setRegisterModalOpen(false)}
            />
        </>
    );
};

export default AuthModals; 